import React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { AppUI, ExerciseUI, VocabUI } from '@seedlang/state';
import Text from 'components/text';
import Link from 'components/link';
import { Constants, Languages, Theme } from '@seedlang/constants';
import Spinner from 'components/spinner';
import { autorun, computed, observable } from 'mobx';
import { isBlank, isPresent } from '@seedlang/utils';
import Filter from 'components/filter';
import { WorksheetVocabWrapper } from '@seedlang/hoc';
import SortSelector from 'components/sort_selector';
import Checkbox from 'components/checkbox';
import AddToReviews from 'components/vocab/add_to_reviews';
import ReactTooltip from 'react-tooltip';
import NoAccessButton from 'components/worksheet/no_access_button';
import WordTags from 'components/worksheet/word_tags';
import WorksheetVocabTargetText from 'components/worksheet/worksheet_vocab_target_text';
import Frequency from 'components/vocab/frequency';
import { SEEDLANG_EXPLAINER_UI_ELEMENT } from 'components/vocab/seedlang_explainer';
import SeedlangExplainerModal from 'components/vocab/seedlang_explainer_modal';
import eventParamsHelper from 'utils/event_params_helper';
import MemrevOnly from 'utils/memrev_only';
import withRouteParams from 'utils/with_route_params';
import { getPodcastAccessSubText } from 'utils/podcast_access_disclaimer';
import { Accordion, Table as MantineTable, Text as MantineText } from '@mantine/core';

const Wrapper = styled.div`

`;

const VocabWrapper = styled.div`
  background: white;
  padding: 20px;
  border-radius: ${props => props.borderRadius};
  border-top-left-radius: ${props => props.borderTopLeftRadius};
  margin-bottom: 10px;
`;

const Table = styled.div`
  -webkit-mask-image: ${props => props.hasAccess ? null : 'linear-gradient(to bottom,black,60%,transparent)'};
  pointer-events: ${props => props.hasAccess ? 'auto' : 'none'};
`;

const HeaderRow = styled.div`
  background: #636363;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  height: 30px;
`;

const HeaderCell = styled.div`
  display: flex;
  align-items: center;
  width: ${props => props.width};
  flex: ${props => props.expand ? 1 : null};
  padding: 0 10px;
  border-right: 1px solid white;
  height: 30px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  border-bottom: 1px solid #cccccc;
  padding-bottom: 5px;
  .register {
    color: ${Theme.gray};
    font-size: 14px;
    font-style: italic;
  }
  .source {
    font-size: 14px;
  }
`;

const Cell = styled.div`
  display: flex;
  align-items: ${props => props.alignItems || 'center'};
  justify-content: ${props => props.justifyContent};
  width: ${props => props.width};
  flex: ${props => props.expand ? 1 : null};
  flex-direction: ${props => props.flexDirection};
  padding: ${props => props.padding || '0 5px'};
  .selection-button {
    width: 100%;
  }
  .icon-wrapper {
    cursor: pointer;
  }
  flex-wrap: wrap;
  column-gap: 14px;
`;

const Word = styled.div`
  .text {
    display: inline-block;
  }
`;

const AdminLink = styled.div`
  margin-top: 5px;
  font-size: 11px;
  text-decoration: underline;
  text-align: center;
  padding: 5px;
`;

const VocabFooter = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  font-size: 14px;
  margin-top: 20px;
`;

const ShowMoreButton = styled.button`
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-decoration: underline;
`;

const FilterRow = styled.div`
  display: flex;
`;

const additionalVocabRows = (htmlText) => {
  if (isBlank(htmlText)) { return []; }

  const htmlWithLineBreaks = htmlText
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li)>/gi, '\n')
    .replace(/<(p|div|li)[^>]*>/gi, '');

  const decodedText = (() => {
    if (typeof document === 'undefined') {
      return htmlWithLineBreaks.replace(/<[^>]*>/g, '');
    }

    const decoder = document.createElement('div');
    decoder.innerHTML = htmlWithLineBreaks;
    return decoder.textContent;
  })();

  return decodedText
    .split('\n')
    .map(line => line.trim())
    .filter(isPresent)
    .map((line) => {
      const separatorMatch = [...line.matchAll(/\s[-–—]\s/g)].pop();
      if (!separatorMatch) {
        return { targetText: line, sourceText: '' };
      }

      const separatorIndex = separatorMatch.index;
      return {
        targetText: line.slice(0, separatorIndex).trim(),
        sourceText: line.slice(separatorIndex + separatorMatch[0].length).trim(),
      };
    });
};

@observer
class WorksheetVocab extends React.Component {
  @observable worksheetWordsAreLoaded = false;
  @observable isLoadingRemainingWords = false;
  @observable showSeedlangExplainerModal = false;

  @computed get createdReviewsCount() {
    const selectedCount = this.props.selectedWordIds?.length || 0;
    const cardType = Constants.REVIEW_CARD_TYPES.find(item => item.id === this.props.cardTypeId);

    if (!cardType) {
      return selectedCount;
    }

    if (cardType.slug === 'gender') {
      return selectedCount - (this.props.selectedWordsExcludedFromGenderTrainer?.length || 0);
    }

    if (cardType.slug === 'plural') {
      return selectedCount - (this.props.selectedWordsExcludedFromPluralTrainer?.length || 0);
    }

    if (cardType.slug === 'conjugation') {
      return selectedCount - (this.props.selectedWordsExcludedFromConjugationTrainer?.length || 0);
    }

    return selectedCount;
  }

  @computed get selectedCardType() {
    return Constants.REVIEW_CARD_TYPES.find(item => item.id === this.props.cardTypeId);
  }

  onCreateReviews = () => {
    if (AppUI.siteIsMemrev) {
      AppUI.createEvent('memrev - click add to reviews button', eventParamsHelper({
        worksheet_id: ExerciseUI.worksheet?.id,
        review_deck_id: this.props.deckId || AppUI.defaultReviewDeckId,
        card_type_id: this.props.cardTypeId,
        card_type_slug: this.selectedCardType?.slug,
        card_type_name: this.selectedCardType?.name,
        selected_words_count: this.props.selectedWordIds?.length || 0,
        reviews_created_count: this.createdReviewsCount,
      }));
      AppUI.createEvent('memrev - create review', eventParamsHelper({
        reviews_created_count: this.createdReviewsCount,
      }));
    }

    this.props.onCreateReviews();
  }

  showSeedlangExplainer = () => {
    if (AppUI.siteIsMemrev && !AppUI.wasUiElementSeen(SEEDLANG_EXPLAINER_UI_ELEMENT)) {
      this.showSeedlangExplainerModal = true;
      AppUI.designateUiElementAsSeen(SEEDLANG_EXPLAINER_UI_ELEMENT);
    }
  }

  onToggleAddWordToDeck = (word) => {
    const wordWasSelected = this.props.selectedWordIds.includes(word?.id);
    this.props.onToggleAddWordToDeck(word);

    if (!wordWasSelected) {
      this.showSeedlangExplainer();
    }
  }

  onToggleAddAllToDeck = () => {
    const allWordsWereSelected = this.props.allToggleSelected;
    this.props.onToggleAddAllToDeck();

    if (!allWordsWereSelected) {
      this.showSeedlangExplainer();
    }
  }

  renderAdditionalVocab = ({ children, ...wrapperProps }) => {
    return (
      <VocabWrapper {...wrapperProps}>
        <Accordion variant='contained' chevronPosition='left' chevronIconSize={20}>
          <Accordion.Item value='additional-vocab'>
            <Accordion.Control>
              <MantineText fw={700}>Additional Vocab</MantineText>
            </Accordion.Control>
            <Accordion.Panel>
              <MantineText size='sm' fw={700} c='dimmed' mb='sm'>
                {ExerciseUI.site.language?.hasLessons ? 'The following terms are not yet available in the Seedlang dictionary or reviews.' : 'The following terms are not yet available in our dictionary.'}
              </MantineText>
              {children}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </VocabWrapper>
    );
  }

  renderAdditionalVocabTable = (htmlText) => {
    const rows = additionalVocabRows(htmlText);

    return (
      <MantineTable striped withTableBorder withColumnBorders>
        <MantineTable.Tbody>
          {
            rows.map((row, index) => (
              <MantineTable.Tr key={`${row.targetText}-${index}`}>
                <MantineTable.Td w='1%' pr='lg' style={{ whiteSpace: 'nowrap' }}>{row.targetText}</MantineTable.Td>
                <MantineTable.Td w='99%'>{row.sourceText}</MantineTable.Td>
              </MantineTable.Tr>
            ))
          }
        </MantineTable.Tbody>
      </MantineTable>
    );
  }

  sourceText = (item) => {
    if (isPresent(item.wordStructure) && isPresent(item.wordStructure.sourceText)) {
      return item.wordStructure.sourceText;
    }
    return item.sourceList;
  }

  @computed get worksheetWordsWorksheetId() {
    if (!ExerciseUI.worksheetWordStore.hasIndexData) return null;
    const idArray = ExerciseUI.worksheetWordStore.indexData.map(item => item.worksheetId);
    return idArray.length > 0 && idArray[0];
  }

  @computed get podcastAccessSubText() {
    return getPodcastAccessSubText(ExerciseUI.worksheet, AppUI.membershipTypeStore.indexData);
  }

  worksheetMatchesRoute = worksheetId => (
    [ExerciseUI.worksheet?.id, ExerciseUI.worksheet?.routeId].includes(worksheetId)
  );

  constructor(props) {
    super();
    AppUI.loadReviewDecks();
    if (
      ExerciseUI.hasWorksheet &&
      this.worksheetMatchesRoute(props.params.worksheetId) &&
      isPresent(ExerciseUI.worksheetWords) &&
      ExerciseUI.worksheetWordStore.ids?.worksheetId === ExerciseUI.worksheet.id
    ) {
      this.worksheetWordsAreLoaded = true;
    }
    this.disposer = autorun(() => {
      if (ExerciseUI.hasWorksheet && !this.worksheetMatchesRoute(props.params.worksheetId)) {
        this.worksheetWordsAreLoaded = false;
        ExerciseUI.worksheetWordStore.clearIndexData();
      }
      if (ExerciseUI.hasWorksheet && this.worksheetMatchesRoute(props.params.worksheetId) && !this.worksheetWordsAreLoaded) {
        props.getWorksheetWords(ExerciseUI.worksheet.id, {clearIndex: true});
        this.worksheetWordsAreLoaded = true;
      }
      if (ExerciseUI.hasWorksheet && VocabUI.worksheetId !== ExerciseUI.worksheetId) {
        VocabUI.worksheetId = ExerciseUI.worksheetId;
      }
      if (AppUI.defaultReviewDeckId && !VocabUI.defaultReviewDeckId) {
        VocabUI.setDefaultReviewDeckId(AppUI.defaultReviewDeckIdForLanguageId(ExerciseUI.languageId));
      }
      if (ExerciseUI.hasWorksheet && isBlank(ExerciseUI.groupId)) {
        ExerciseUI.set('groupId', ExerciseUI.worksheet.groupId);
      }
    });
  }

  componentDidMount() {
    if (!AppUI.membershipTypeStore.hasIndexData) {
      AppUI.membershipTypeStore.getIndex();
    }
  }

  componentWillUnmount() {
    if (this.disposer) {
      this.disposer();
    }
    VocabUI.worksheetId = null;
    VocabUI.selectedWords = [];
    VocabUI.allToggleSelected = false;
    VocabUI.updateCardType();
  }
  loadRemainingWords = () => {
    this.isLoadingRemainingWords = true;
    this.props.loadRemainingWords(ExerciseUI.worksheetId, {
      callback: () => { this.isLoadingRemainingWords = false }
    });
  }

  @computed get worksheetHasAlternateText() {
    if (ExerciseUI.worksheetWordStore.hasIndexData) {
      return ExerciseUI.filteredWorksheetWords.some(item => isPresent(item.word?.alternateTargetText));
    }
    return false;
  }

  @computed get reviewsAreEnabled() {
    return ExerciseUI.site.language?.hasLessons;
  }

  @computed get showFrequencyColumn() {
    if (!AppUI.site.showFrequencySort) return false;
    return /frequency/i.test(this.props.vocabSort);
  }

  @computed get showLevelColumn() {
    if (!AppUI.site.showLevelColumn) return false;
    return !AppUI.layout.isMobile || !this.showFrequencyColumn;
  }

  @computed get compactView() {
    let colCount = 2;
    if (this.reviewsAreEnabled) { colCount += 1; }
    if (this.showLevelColumn) { colCount += 1; }
    if (this.showFrequencyColumn) { colCount += 1; }
    if (AppUI.site.showAlternateTargetText && this.worksheetHasAlternateText) { colCount += 1; }
    return AppUI.layout.isMobile && colCount >= 3;
  }

  render() {
    return (
      <Wrapper>
        <MemrevOnly>
          <SeedlangExplainerModal
            isPodcastEpisode={Boolean(ExerciseUI.worksheet?.podcastEpisode)}
            languageName={AppUI.site?.language?.name}
            opened={this.showSeedlangExplainerModal}
            onClose={() => this.showSeedlangExplainerModal = false}
          />
        </MemrevOnly>
        {
          ExerciseUI.hasWorksheet && ExerciseUI.worksheet.publishWords &&
          <VocabWrapper
            borderRadius={this.props.borderRadius}
          >
            {ExerciseUI.worksheet?.accessStatus?.hasVocabAccess &&
              <FilterRow>
                {
                  isPresent(this.props.filterSettings) && isPresent(ExerciseUI.userGroup) &&
                    <Filter
                      filterName='Vocab Filters'
                      noFiltersSelectedMessage={this.props.noFiltersSelectedMessage}
                      filterSettings={this.props.filterSettings}
                      onSetFilter={this.props.onSetFilter}
                      // searchQuery={this.props.searchQuery}
                      hasActiveFilter={this.props.hasActiveFilter}
                      // onToggleFiltersExpanded={this.props.onToggleFiltersExpanded}
                      setShowFilterOptions={this.props.setShowFilterOptions}
                      showFilterOptions={this.props.showFilterOptions}
                      filteredItemsCount={ExerciseUI.worksheetWordStore.totalCount}
                      filteredItemsType='Word'
                      marginBottom='10px'
                      width={isPresent(this.props.sortOptions) ? '80%' : '100%'}
                    />
                }
                {
                  isPresent(this.props.sortOptions) && isPresent(ExerciseUI.userGroup) &&
                    <SortSelector
                      margin={isPresent(this.props.filterSettings) ? '10px 0 10px 10px' : '10px 0 10px 0'}
                      width={isPresent(this.props.filterSettings) ? '20%' : null}
                      onToggleSort={this.props.onToggleSort}
                      sortOrder={this.props.sortOrder}
                      sortOptions={this.props.sortOptions}
                      selectedSortLabel={this.props.selectedSortLabel}
                      onSetSort={this.props.onSetSort}
                    />
                }
              </FilterRow>
            }
            <AddToReviews
              buttonText='Add to Seedlang'
              isPodcastEpisode={Boolean(ExerciseUI.worksheet.podcastEpisode)}
              reviewDeckOptions={ExerciseUI.reviewDeckOptions}
              deckId={this.props.deckId}
              onSetDeckId={this.props.onSetDeckId}
              cardTypeOptions={this.props.cardTypeOptions}
              cardTypeId={this.props.cardTypeId}
              onSetCardTypeId={this.props.onSetCardTypeId}
              onCreateReviews={this.onCreateReviews}
              deckButtonDisabled={this.props.deckButtonDisabled}
              selectedWordsExcludedFromGenderTrainer={this.props.selectedWordsExcludedFromGenderTrainer}
              selectedWordsExcludedFromPluralTrainer={this.props.selectedWordsExcludedFromPluralTrainer}
              selectedWordsExcludedFromConjugationTrainer={this.props.selectedWordsExcludedFromConjugationTrainer}
              showReviewsCreatedMessage={this.props.createReviewsFinished || this.props.createReviewsIsSlow}
              selectedWordIds={this.props.selectedWordIds}
              confirmationMessage={`${this.props.createReviewsIsSlow ? 'Your reviews are being created' : 'Your reviews were successfully created'}. <a href='https://seedlang.com/reviews/home' target='_blank'>Go to your reviews</a>`}
            />
            { !ExerciseUI.worksheetWordStore.hasIndexData && <Spinner /> }
            {
              ExerciseUI.worksheetWordStore.hasIndexData &&
              <Table
                hasAccess={ExerciseUI.worksheet?.accessStatus?.hasVocabAccess}
              >
                <HeaderRow>
                  {
                    this.reviewsAreEnabled &&
                      <HeaderCell
                        width='40px'
                      >
                        <Checkbox
                          background='#d0d0d0'
                          selectedBackground='#232323'
                          onClick={this.onToggleAddAllToDeck}
                          value={this.props.allToggleSelected}
                        />
                      </HeaderCell>
                  }
                  <HeaderCell
                    expand={!ExerciseUI.languageId?.startsWith('EN')}
                    width={ExerciseUI.languageId?.startsWith('EN') ? AppUI.layout.isMobile ? '100px' : '250px' : null}
                  >
                    {
                      !this.compactView &&
                        <Text
                          color='white'
                          fontSize='12px'
                          bold
                        >
                          { isPresent(ExerciseUI.languageId) ? ExerciseUI.languageId?.startsWith('EN') ? 'English' : Languages[ExerciseUI.languageId] : 'Target Language' }
                        </Text>
                    }
                    {
                      this.compactView &&
                        <Text
                          color='white'
                          fontSize='12px'
                          bold
                        >
                          Word
                        </Text>
                    }
                  </HeaderCell>
                  {
                    AppUI.site.showAlternateTargetText && this.worksheetHasAlternateText &&
                      <HeaderCell
                        expand
                      >
                        <Text
                          color='white'
                          fontSize='12px'
                          bold
                        >
                          {isPresent(AppUI.site.alternateTargetTextName) ? AppUI.site.alternateTargetTextName : 'Romanized spelling'}
                        </Text>
                      </HeaderCell>
                  }
                  {
                    !this.compactView &&
                      <HeaderCell
                        expand
                      >
                        <Text
                          color='white'
                          fontSize='12px'
                          bold
                        >
                          {ExerciseUI.languageId?.startsWith('EN') ? 'Definition' : 'English'}
                        </Text>
                      </HeaderCell>
                  }
                  {
                    this.showLevelColumn &&
                      <HeaderCell
                        width='60px'
                      >
                        <Text
                          color='white'
                          fontSize='12px'
                          bold
                        >
                          Level
                        </Text>
                      </HeaderCell>
                  }
                  {
                    this.showFrequencyColumn &&
                      <HeaderCell width='60px'>
                        <Text
                          color='white'
                          fontSize='12px'
                          bold
                        >
                          Freq.
                        </Text>
                      </HeaderCell>
                  }
                </HeaderRow>
                {
                  // also in worksheet_exports/show.haml
                  ExerciseUI.filteredWorksheetWords.slice(0, ExerciseUI.worksheet?.accessStatus?.hasVocabAccess ? ExerciseUI.filteredWorksheetWords.length : 5).map(item => {
                    return (
                      <Row
                        key={item.id}
                      >
                        {
                          this.reviewsAreEnabled &&
                            <Cell
                              width='40px'
                              padding='0 10px'
                              data-tip
                              data-for={`incompatible-${item.id}`}
                            >
                              <Checkbox
                                width='22px'
                                onClick={() => this.onToggleAddWordToDeck(item.word)}
                                value={this.props.selectedWordIds.includes(item.word?.id)}
                                showLock={!this.props.selectedCardTypeIsCompatible(item.word)}
                              />
                              {
                                !this.props.selectedCardTypeIsCompatible(item.word) &&
                                  <ReactTooltip
                                    place='right'
                                    type='dark'
                                    effect='solid'
                                    id={`incompatible-${item.id}`}
                                    class='custom-tooltip'
                                  >
                                    <div>This word is not compatible</div>
                                    <div>{`with ${Constants.REVIEW_CARD_TYPES.find(item => item.id === this.props.cardTypeId).name} cards.`}</div>
                                  </ReactTooltip>
                              }
                            </Cell>
                        }
                        <Cell
                          expand={!ExerciseUI.languageId?.startsWith('EN')}
                          width={ExerciseUI.languageId?.startsWith('EN') ? AppUI.layout.isMobile ? '100px' : '250px' : null}
                          flexDirection={this.compactView ? 'column' : 'row'}
                          alignItems={this.compactView ? 'left' : null}
                        >
                          {
                            AppUI.site.showWordPages &&
                              <Word>
                                <Link
                                  state={{worksheetNavigation: {
                                    worksheetId: this.props.params.worksheetId,
                                    filters: { ...this.props.filters },
                                    sort: this.props.vocabSort,
                                  }}}
                                  to={{name: `${ExerciseUI.worksheetNamespace}.vocab.word`,
                                    params: {
                                      worksheetId: this.props.params.worksheetId,
                                      wordId: item.word?.id,
                                    },
                                  }}
                                >
                                  <WorksheetVocabTargetText worksheetWord={item} />
                                </Link>
                              </Word>
                          }
                          {
                            !AppUI.site.showWordPages &&
                              <WorksheetVocabTargetText worksheetWord={item} />
                          }
                          {
                            item.word?.register &&
                              <div
                                className='register'
                                fontSize='14px'
                              >
                                {item.word?.register}
                              </div>
                          }
                          {
                            this.compactView &&
                              <div
                                className='source'
                              >
                                {
                                  !ExerciseUI.languageId?.startsWith('EN') &&
                                  <div
                                    dangerouslySetInnerHTML={{__html: this.sourceText(item)}}
                                  />
                                }
                                {ExerciseUI.languageId?.startsWith('EN') && item.word.definition}
                              </div>
                          }
                          <WordTags word={item.word} groupId={ExerciseUI.site.groupId} />
                        </Cell>
                        {
                          AppUI.site.showAlternateTargetText && this.worksheetHasAlternateText &&
                          <Cell
                            expand
                          >
                            <Text
                              fontSize='16px'
                            >
                              {item.word?.alternateTargetText}
                            </Text>
                          </Cell>
                        }
                        {
                          !this.compactView &&
                            <Cell
                              expand
                            >
                              <Text
                                fontSize='16px'
                              >
                                {
                                  !ExerciseUI.languageId?.startsWith('EN') &&
                                  <div
                                    dangerouslySetInnerHTML={{__html: this.sourceText(item)}}
                                  />
                                }
                                {ExerciseUI.languageId?.startsWith('EN') && item.word.definition}
                              </Text>
                            </Cell>
                        }
                        {
                          this.showLevelColumn &&
                            <Cell
                              width='60px'
                              justifyContent='center'
                            >
                              <Text
                                fontSize='14px'
                                color='#353535'
                              >
                                {item.word?.level?.abbreviation}
                              </Text>
                            </Cell>
                        }
                        {
                          this.showFrequencyColumn &&
                            <Cell width='60px' justifyContent='center'>
                              {
                                isPresent(item.word?.frequencyRanking) &&
                                  <Frequency word={item.word} />
                              }
                            </Cell>
                        }
                      </Row>
                    );
                  })
                }
              </Table>
            }
            {
              ExerciseUI.hasWorksheet && !ExerciseUI.worksheet?.accessStatus?.hasVocabAccess &&
                <NoAccessButton
                  reason={ExerciseUI.worksheet?.accessStatus?.reason}
                  subText={this.podcastAccessSubText}
                />
            }
            {
              ExerciseUI.worksheet?.accessStatus?.hasVocabAccess &&
                <VocabFooter>
                  { this.isLoadingRemainingWords && <Spinner/> }
                  {
                    !this.isLoadingRemainingWords && ExerciseUI.worksheetWordStore.hasIndexData && !this.props.allWordsLoaded &&
                      <ShowMoreButton
                        type='button'
                        onClick={this.loadRemainingWords}
                      >
                        Show More
                      </ShowMoreButton>
                  }
                </VocabFooter>
            }
            {
              (ExerciseUI.worksheetWordStore.hasIndexData || ExerciseUI.worksheetWordStore.totalCount === 0) && isPresent(this.props.filters) &&
                <Text italic center fontSize='12px' margin='10px 0 0 0'>{`Displaying ${ExerciseUI.worksheetWordStore.totalCount} of ${ExerciseUI.worksheet.worksheetWordsCount} words with these filters.`}</Text>
            }
          </VocabWrapper>
        }
        {
          !ExerciseUI.worksheetWordStore.hasOutstandingRequests &&ExerciseUI.worksheet.publishUnprocessedVocab && isPresent(ExerciseUI.worksheet.unprocessedVocab) && ExerciseUI.worksheet?.accessStatus?.hasVocabAccess &&
            this.renderAdditionalVocab({
              children: this.renderAdditionalVocabTable(ExerciseUI.worksheet.unprocessedVocab),
              borderTopLeftRadius: isBlank(ExerciseUI.filteredWorksheetWords) || !ExerciseUI.worksheet.publishWords ? 0 : '10px',
              borderRadius: this.props.standaloneSectionBorderRadius,
            })
        }
        {
          !ExerciseUI.worksheet?.accessStatus?.hasVocabAccess && ExerciseUI.worksheet.publishUnprocessedVocab && isPresent(ExerciseUI.worksheet.unprocessedVocab) && (isBlank(ExerciseUI.filteredWorksheetWords) || !ExerciseUI.worksheet.publishWords) &&
            this.renderAdditionalVocab({
              children: (
                <React.Fragment>
                  {this.renderAdditionalVocabTable(ExerciseUI.worksheet.unprocessedVocab)}
                  <NoAccessButton
                    reason={ExerciseUI.worksheet?.accessStatus?.reason}
                    subText={this.podcastAccessSubText}
                  />
                </React.Fragment>
              ),
              borderTopLeftRadius: '0',
              borderRadius: '10px',
            })
        }
        {
          ExerciseUI.user.anyAdmin && isPresent(ExerciseUI.worksheet) &&
            <AdminLink>
              <Link
                to={{name: 'creator.vocab.edit', params: { worksheetId: ExerciseUI.worksheet.id }}}
              >
                Admin Link
              </Link>
            </AdminLink>
        }
        </Wrapper>
      );
    }
  }

  export default withRouteParams(WorksheetVocabWrapper(WorksheetVocab));
